import {
  Host,
  Input,
  Optional,
  SkipSelf,
  ViewChild,
  OnChanges,
  Component,
  ElementRef,
  forwardRef,
  SimpleChanges,
} from '@angular/core';

import { ControlContainer, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';

import { BehaviorSubject } from 'rxjs';

import { BaseControlComponent, AutocompleteControl } from '../../classes';

import { AutocompleteOptionInterface, AutocompleteGroupInterface } from './interfaces';

type AutocompleteValueType = string | number | string[] | number[] | null;

@Component({
  selector: 'app-autocomplete',
  templateUrl: './autocomplete.component.html',
  styleUrls: ['./autocomplete.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteComponent),
      multi: true,
    },
  ],
})
export class AutocompleteComponent
  extends BaseControlComponent<AutocompleteValueType, AutocompleteValueType, AutocompleteControl>
  implements OnChanges {
  public selectedOption: AutocompleteOptionInterface | null;
  public autocompleteOptions: BehaviorSubject<AutocompleteOptionInterface[]> | undefined;
  public autocompleteGroups: BehaviorSubject<AutocompleteGroupInterface[]> | undefined;

  @Input() public outlined: boolean | undefined;

  @ViewChild('input', { static: true }) public inputReference: ElementRef | undefined;
  @ViewChild(MatAutocompleteTrigger, { static: true }) autocompleteTrigger: MatAutocompleteTrigger | undefined;

  constructor(
    @Optional()
    @Host()
    @SkipSelf()
    protected controlContainer: ControlContainer,
  ) {
    super(controlContainer);

    this.selectedOption = null;
  }

  private getOptions(): AutocompleteOptionInterface[] {
    if (!this.asFormsControl().grouped) {
      return this.asFormsControl().options;
    }

    return this.asFormsControl().groups.reduce((groupOptions: AutocompleteOptionInterface[], group: AutocompleteGroupInterface) => {
      groupOptions.push(...group.options.items);

      return groupOptions;
    }, []);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ngOnChanges(changes: SimpleChanges): void {
    this.asFormsControl();

    if (typeof this.asFormsControl().checkForEmptyInputReference !== 'undefined') {
      this.asFormsControl().checkForEmptyInputReference = this.checkForEmptyInputReference.bind(this);
    }

    if (!this.asFormsControl().grouped) {
      this.autocompleteOptions = new BehaviorSubject(this.asFormsControl().options);
    } else {
      this.autocompleteGroups = new BehaviorSubject(this.asFormsControl().groups);
    }

    this.checkInvalid();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filterOptions(event: any): void {
    const { target: { value } } = event;

    this.executeFilter(value);
  }

  filterOptionsOnPaste(event: ClipboardEvent): void {
    const value = !event.clipboardData ? '' : event.clipboardData.getData('text');

    this.executeFilter(value);
  }

  executeFilter(value: string): void {
    /**
     * Parse special symbols for regex.
     */
    const pattern = value.toLowerCase().replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');

    const regex = new RegExp('.*' + pattern + '.*');

    let options: AutocompleteOptionInterface[] = [];

    if (!this.asFormsControl().grouped) {
      options = this.asFormsControl().options.filter((option: AutocompleteOptionInterface) => {
        return regex.test(option.label.toLowerCase());
      });

      if (this.autocompleteOptions) {
        this.autocompleteOptions.next(options);
      }
    } else {
      const controlValues = this.asFormsControl().groups.filter((group: AutocompleteGroupInterface) => {
        const optionMatches = group.options.items.filter((option: AutocompleteOptionInterface) => {
          options.push(option);

          return regex.test(option.label.toLowerCase());
        });

        return regex.test(group.options.name.toLowerCase()) || optionMatches.length !== 0;
      });

      if (this.autocompleteGroups) {
        this.autocompleteGroups.next(controlValues);
      }
    }

    if (this.control) {
      this.control.resetErrors();
    }

    if (options.length !== 1) {
      this.asFormsControl().resetValue();
      this.changeValue(null);
      this.selectedOption = null;
    } else {
      if (options[0].label !== value) {
        this.asFormsControl().resetValue();
        this.changeValue(null);
        this.selectedOption = null;
      } else {
        this.changeValue(options[0].value);

        if (this.autocompleteTrigger) {
          this.autocompleteTrigger.closePanel();
        }
      }
    }

    setTimeout(() => this.updateAutocompletePosition(), 1);
  }

  updateAutocompletePosition(): void {
    if (this.autocompleteTrigger) {
      this.autocompleteTrigger.updatePosition();
    }
  }

  checkForEmptyInputReference(): boolean {
    if (this.inputReference) {
      const { nativeElement: { value } } = this.inputReference;

      return value === '';
    }

    return true;
  }

  selectOption(event: MatAutocompleteSelectedEvent) {
    if (this.inputReference) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.inputReference.nativeElement.value = event.option.viewValue;
    }

    const filteredOptions = this.getOptions().find((option: AutocompleteOptionInterface) => {
      return event.option.value === option.value;
    });

    this.selectedOption = filteredOptions ?? null;

    this.changeValue(event.option.value);
  }

  changeValue(value: AutocompleteValueType): void {
    if (this.onChange) {
      this.onChange(value);
    }

    this.checkInvalid();

    this.control?.resetErrors();

    super.writeValue(value);
  }

  writeValue(value: AutocompleteValueType): void {
    const filteredOptions = this.getOptions().filter((option: AutocompleteOptionInterface) => {
      return value === option.value;
    });

    if (this.inputReference) {
      if (filteredOptions.length !== 0) {
        this.selectedOption = filteredOptions[0];
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.inputReference.nativeElement.value = filteredOptions[0].label;
        super.writeValue(filteredOptions[0].value);
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.inputReference.nativeElement.value = '';
        this.selectedOption = null;

        if (typeof value !== 'string') {
          super.writeValue(null);
        } else {
          super.writeValue('');
        }
      }
    }

    this.checkInvalid();
    this.control?.resetErrors();
  }

  asFormsControl(): AutocompleteControl {
    if (!this.control || !(this.control instanceof AutocompleteControl)) {
      this.control = new AutocompleteControl({
        name: '',
        value: '',
        placeholder: '',
        validators: [],
        componentType: 0,
        invalid: false,
        errors: [],
        options: [],
        grouped: false,
        groups: [],
        checkForEmptyInputReference: undefined,
      });
    }

    return this.control as AutocompleteControl;
  }

  trackById(index: number, item: AutocompleteOptionInterface): AutocompleteValueType {
    return item.value;
  }
}
